import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

import { BarPlot, ChartContainer, ChartsXAxis } from '@mui/x-charts'
import { useContainerDimensions } from '../../../../hooks/useContainerDimensions'

import { DataSet, DoubleBarChartMXUIProps } from '../../../../types/Charts/BarChart'

import { CustomDoubleBarPlotMXUI } from './CustomDoubleBarPlotMXUI'
import { DoubleBarChartLegendMXUI } from './DoubleBarChartLegendMXUI'

export const DoubleBarChartMXUI = ({
  barGapRatio = 0.005,
  categoryGapRatio = 0.16,
  colors,
  data,
  isMini = false,
  legendLabelComparison,
  legendLabelMain,
  margin = {
    bottom: 16,
    left: 24,
    right: 24,
    top: 0,
  },
  selectedTabIndex,
  onBarClick,
}: DoubleBarChartMXUIProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)

  const handleBarClick = ({ dataIndex, seriesId }: { dataIndex: number; seriesId: string }) => {
    const seriesType = seriesId.toString().split('-').pop()
    onBarClick?.(dataIndex * 2 + Number(seriesType))
  }

  return (
    <Stack sx={{ mb: 0, width: '100%', p: 0 }}>
      <Box
        ref={containerRef}
        sx={{
          height: isMini ? 120 : 172,
          touchAction: 'pan-y',
          width: '100%',
          p: 0,
          m: 0,
          '& svg': {
            touchAction: 'pan-y',
            userSelect: 'none',
          },
          '& .recharts-wrapper': {
            touchAction: 'pan-y',
          },
        }}
      >
        <ChartContainer
          colors={colors}
          dataset={data as any[]}
          height={height}
          margin={margin}
          series={[
            { dataKey: 'main', type: 'bar' },
            { dataKey: 'comparison', type: 'bar' },
          ]}
          width={width}
          xAxis={[{ barGapRatio, categoryGapRatio, dataKey: 'label', scaleType: 'band' }]}
          yAxis={[{ width: 0 }]}
        >
          <ChartsXAxis
            disableTicks={false}
            slots={{
              axisTickLabel: ({ text, ...restProps }) => {
                const label =
                  selectedTabIndex !== undefined ? data[selectedTabIndex]?.label : undefined
                return (
                  <text
                    {...restProps}
                    style={
                      {
                        ...restProps.style,
                        fontWeight: label === text ? 700 : 400,
                      } as React.CSSProperties
                    }
                  >
                    {text}
                  </text>
                )
              },
            }}
            sx={{
              '.MuiChartsAxis-line': {
                stroke: 'border.darker',
              },
            }}
            tickLabelStyle={{
              fontSize: 11,
              fill: 'text.secondary',
              fontWeight: 400,
            }}
          />
          <BarPlot
            onItemClick={(_, { dataIndex, seriesId }) =>
              handleBarClick({ dataIndex, seriesId: seriesId.toString() })
            }
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                return (
                  <CustomDoubleBarPlotMXUI
                    {...defaultProps}
                    color={
                      selectedTabIndex !== ownerState.dataIndex
                        ? `${ownerState.color}50`
                        : ownerState.color
                    }
                    data={data as DataSet[]}
                    series={{
                      index: ownerState.dataIndex,
                      type: Number(ownerState.id.toString().split('-').pop()),
                    }}
                    style={{
                      height: props.height,
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />
        </ChartContainer>
      </Box>

      {(legendLabelComparison || legendLabelMain) && (
        <DoubleBarChartLegendMXUI
          comparisonColor={colors[1]}
          legendLabelComparison={legendLabelComparison}
          legendLabelMain={legendLabelMain}
          legendStyle={{ ml: margin.left }}
          mainColor={colors[0]}
        />
      )}
    </Stack>
  )
}
